<template>
    <div class="grid-skeleton">
        <v-skeleton-loader class="grid-skeleton__loader"
                           :type="skeletonType"
                           :types="skeletonTypes" />

        <div v-if="label" class="grid-skeleton__caption">
            <v-progress-circular indeterminate size="16" width="2" color="primary" class="me-2" />
            <span>{{ label }}</span>
        </div>
    </div>
</template>

<script>
    /**
     * Content placeholder for a paged data grid.
     *
     * Mirrors the shape of the loaded table - title row, header row, body rows and
     * footer - so the page keeps its height while the request is in flight and the
     * content does not jump when the real grid replaces it.
     *
     * Built on Vuetify's v-skeleton-loader, whose table presets are re-declared here
     * with the requested column count.
     */
    export default {

        props: {

            // body rows to render; pick roughly the page size of the real grid
            rows: {
                type: Number,
                default: 6
            },

            columns: {
                type: Number,
                default: 5
            },

            // placeholder for a heading above the grid
            showTitle: {
                type: Boolean,
                default: true
            },

            // placeholder for the pagination footer
            showFooter: {
                type: Boolean,
                default: true
            },

            // optional caption shown under the placeholder, e.g. "Loading results…"
            label: {
                type: String,
                default: ''
            }
        },

        computed: {

            skeletonType() {
                const parts = []

                if (this.showTitle) {
                    parts.push('table-heading')
                }

                parts.push('table-thead')
                parts.push(`table-row-divider@${this.rows}`)

                if (this.showFooter) {
                    parts.push('table-tfoot')
                }

                return parts.join(', ')
            },

            // Vuetify's defaults are hard-coded to six columns
            skeletonTypes() {
                return {
                    'table-thead': `heading@${this.columns}`,
                    'table-row': `table-cell@${this.columns}`
                }
            }
        }
    }
</script>

<style>
    .grid-skeleton {
        --grid-skeleton-border: #e4e9ee;
        --grid-skeleton-muted: #78899a;
    }

    .grid-skeleton .grid-skeleton__loader,
    .grid-skeleton .v-skeleton-loader__table-heading,
    .grid-skeleton .v-skeleton-loader__table-thead,
    .grid-skeleton .v-skeleton-loader__table-tbody,
    .grid-skeleton .v-skeleton-loader__table-tfoot {
        background: transparent !important;
        box-shadow: none !important;
    }

    /* title row */
    .grid-skeleton .v-skeleton-loader__table-heading {
        padding: 0 4px 16px;
    }

    .grid-skeleton .v-skeleton-loader__table-heading .v-skeleton-loader__heading {
        margin: 0;
    }

    .grid-skeleton .v-skeleton-loader__table-heading .v-skeleton-loader__text {
        margin: 0;
    }

    /* header row - flex-start + gap keeps the columns evenly spread in LTR and RTL */
    .grid-skeleton .v-skeleton-loader__table-thead {
        gap: 16px;
        justify-content: flex-start;
        padding: 14px 16px;
        border-bottom: 1px solid var(--grid-skeleton-border);
    }

    .grid-skeleton .v-skeleton-loader__table-thead .v-skeleton-loader__heading {
        flex: 1 1 0;
        width: auto;
        max-width: none;
        height: 12px;
        margin: 0;
    }

    /* body rows */
    .grid-skeleton .v-skeleton-loader__table-tbody {
        padding: 0;
    }

    .grid-skeleton .v-skeleton-loader__table-row {
        gap: 16px;
        justify-content: flex-start;
        padding: 0 16px;
    }

    .grid-skeleton .v-skeleton-loader__table-cell {
        flex: 1 1 0;
        width: auto;
        min-width: 0;
        height: 44px;
    }

    .grid-skeleton .v-skeleton-loader__table-cell .v-skeleton-loader__text {
        width: 100%;
        max-width: none;
        margin: 0;
    }

    /* staggered widths read as text rather than as solid blocks */
    .grid-skeleton .v-skeleton-loader__table-cell:nth-child(3n) .v-skeleton-loader__text {
        width: 65%;
    }

    .grid-skeleton .v-skeleton-loader__table-cell:nth-child(3n + 2) .v-skeleton-loader__text {
        width: 85%;
    }

    .grid-skeleton .v-skeleton-loader__divider {
        margin: 0 16px;
    }

    /* footer */
    .grid-skeleton .v-skeleton-loader__table-tfoot {
        gap: 12px;
        padding: 14px 16px 0;
        border-top: 1px solid var(--grid-skeleton-border);
    }

    .grid-skeleton .v-skeleton-loader__table-tfoot .v-skeleton-loader__text {
        margin: 0;
    }

    /* caption */
    .grid-skeleton .grid-skeleton__caption {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 18px 16px 4px;
        color: var(--grid-skeleton-muted);
        font-size: 13px;
        line-height: 1.5;
    }

    @media (max-width: 599px) {
        .grid-skeleton .v-skeleton-loader__table-thead,
        .grid-skeleton .v-skeleton-loader__table-row {
            gap: 12px;
            padding-left: 8px;
            padding-right: 8px;
        }

        /* a narrow viewport would only show the leading columns of the real table */
        .grid-skeleton .v-skeleton-loader__table-cell:nth-child(n + 4),
        .grid-skeleton .v-skeleton-loader__table-thead .v-skeleton-loader__heading:nth-child(n + 4) {
            display: none;
        }

        .grid-skeleton .v-skeleton-loader__table-cell {
            height: 40px;
        }
    }
</style>
